import React, { useEffect, useMemo, useState } from 'react'
import Header from '../components/ui/Header'
import Card, { CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Icon from '../components/AppIcon'
import UsersPanel from '../components/control/UsersPanel'
import HealthChecks from '../components/control/HealthChecks'
import IntegrationPanel from '../components/control/integrations/IntegrationPanel'
import OperatorsPanel from '../components/control/OperatorsPanel'
import LogsPanel from '../components/control/LogsPanel'
import { getIntegrationMeta, setIntegrationConfig, testIntegration } from '../lib/admin-api'
import { localSecretsMode } from '../lib/local-secrets-client'
import { useNavigate } from 'react-router-dom'

export default function ControlHub({
  embedded = false,
  activeSection = null,
  hideHeader = false,
  hideTabs = false,
}){
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(activeSection || 'overview')
  const [intg, setIntg] = useState({
    shopify:{ meta:null, loading:false },
    claude:{ meta:null, loading:false },
    kie_ai:{ meta:null, loading:false },
    google_drive:{ meta:null, loading:false },
    google_sheets:{ meta:null, loading:false },
    google_calendar:{ meta:null, loading:false },
    podium:{ meta:null, loading:false },
    metal_prices:{ meta:null, loading:false },
  })
  const up = (k, p) => setIntg(prev => ({ ...prev, [k]: { ...prev[k], ...p } }))

  async function refresh(name){
    up(name,{loading:true});
    const meta = await getIntegrationMeta(name);
    up(name,{loading:false, meta});
  }
  async function save(name, config){
    up(name,{loading:true});
    await setIntegrationConfig(name, config);
    await refresh(name);
  }
  async function test(name, payload){
    up(name,{loading:true});
    const res = await testIntegration({ name, ...payload });
    const meta = intg[name].meta || {};
    up(name,{loading:false, meta:{ ...meta, result:res, status: res.ok?'OK':'Error', latencyMs: res.latencyMs, lastChecked: new Date().toISOString() }});
  }

  const integrationsList = ['shopify', 'claude', 'kie_ai', 'google_drive', 'google_sheets', 'google_calendar', 'podium', 'metal_prices']

  useEffect(() => {
    if (activeSection) setActiveTab(activeSection)
  }, [activeSection])

  useEffect(()=>{
    (async()=>{
      for (const n of integrationsList) {
        await refresh(n)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'users', label: 'Users & Roles', icon: 'Users' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug' },
    { id: 'system', label: 'System & Logs', icon: 'Settings' },
  ]

  const Wrapper = embedded ? 'div' : 'div'
  const wrapperClass = embedded ? 'space-y-6' : 'min-h-screen bg-background'
  const Content = embedded ? 'div' : 'main'
  const contentClass = embedded ? '' : 'container mx-auto px-4 md:px-6 py-8 max-w-7xl'
  const titleClass = embedded ? 'text-2xl' : 'text-4xl'
  const subtitleClass = embedded ? 'text-sm' : 'text-lg'
  const headerMargin = embedded ? 'mb-6' : 'mb-8'

  return (
    <Wrapper className={wrapperClass}>
      {!embedded && <Header />}
      <Content className={contentClass}>
        {/* Page Header */}
        {!hideHeader && (
          <div className={`${headerMargin} animate-slide-up`}>
            <h1 className={`${titleClass} font-semibold text-foreground mb-2`}>
              {embedded ? 'Admin Control Hub' : 'Control Hub'}
            </h1>
            <p className={`text-muted-foreground ${subtitleClass}`}>
              Admin control center • System diagnostics • Integration management
            </p>
          </div>
        )}

        {/* Dev Mode Banner */}
        {localSecretsMode && (
          <Card variant="solid" className="mb-6" padding="md">
            <div className="flex items-start gap-3">
              <Icon name="AlertTriangle" size={24} className="text-warning flex-shrink-0" />
              <div>
                <p className="font-semibold text-warning mb-1">Development Mode Active</p>
                <p className="text-sm text-muted-foreground">
                  Secrets are stored locally in <code className="px-2 py-0.5 bg-muted rounded font-mono text-xs">.secrets.local.json</code> and tests run via a local proxy. Nothing is uploaded or committed.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Tab Navigation */}
        {!hideTabs && (
          <div className="mb-6 border-b border-border">
            <div className="flex gap-6 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`pb-3 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab.icon} size={16} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fade-in">
              {/* Quick Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <QuickCard title="App Version" icon="Code">
                  <div className="text-sm text-muted-foreground mb-1">Build Environment</div>
                  <div className="font-mono text-2xl font-bold text-foreground">
                    {import.meta.env.MODE || 'dev'}
                  </div>
                </QuickCard>
                <QuickCard title="Cloud Functions" icon="Zap">
                  <div className="text-sm text-muted-foreground mb-2">API Status</div>
                  <div className="flex items-center gap-2">
                    <StatusDot ok />
                    <span className="font-semibold">Reachable</span>
                  </div>
                </QuickCard>
                <QuickCard title="Firebase Services" icon="Database">
                  <div className="flex items-center gap-4 mt-2">
                    <div className="flex items-center gap-2">
                      <StatusDot ok />
                      <span className="text-sm">Firestore</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusDot ok />
                      <span className="text-sm">Storage</span>
                    </div>
                  </div>
                </QuickCard>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <QuickActionCard
                  title="Jobs"
                  description="Job tracking and scheduling (coming soon)"
                  icon="CalendarDays"
                  onClick={() => navigate('/jobs-management')}
                />
                <QuickActionCard
                  title="Exchange Desk"
                  description="Gold & silver buying operations"
                  icon="Scale"
                  onClick={() => navigate('/exchange-desk')}
                />
                <QuickActionCard
                  title="Product Listing"
                  description="Product catalog and publishing"
                  icon="Tag"
                  onClick={() => navigate('/product-listing')}
                />
                <QuickActionCard
                  title="Settings"
                  description="App configuration and branding"
                  icon="Settings"
                  onClick={() => navigate('/settings')}
                />
              </div>

              {/* System Health */}
              <HealthChecks />
            </div>
          )}

          {/* Users & Roles Tab */}
          {activeTab === 'users' && (
            <div className="space-y-6 animate-fade-in">
              <UsersPanel />
              <OperatorsPanel />
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div className="space-y-6 animate-fade-in">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">API Integrations</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Configure API keys and test connections. Keys are masked for security.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RefreshCw"
                    onClick={() => integrationsList.forEach(n => refresh(n))}
                  >
                    Refresh All
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    iconName="Zap"
                    onClick={() => integrationsList.forEach(n => test(n, (intg[n]?.meta?.config || {})))}
                  >
                    Test All
                  </Button>
                </div>
              </div>

              {/* Quick Status */}
              <div className="flex flex-wrap gap-3">
                {integrationsList.map((name) => {
                  const isConnected = intg[name]?.meta?.status === 'OK' || intg[name]?.meta?.hasSecret
                  return (
                    <div
                      key={name}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        isConnected
                          ? 'bg-success/10 text-success'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success' : 'bg-muted-foreground'}`} />
                      {name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </div>
                  )
                })}
              </div>

              {/* Integration Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {integrationsList.map((name) => (
                  <IntegrationPanel
                    key={name}
                    name={name}
                    meta={{
                      config: intg[name]?.meta?.config || {},
                      hasSecret: !!intg[name]?.meta?.hasSecret,
                      status: intg[name]?.meta?.status || 'Unknown',
                      lastChecked: intg[name]?.meta?.lastChecked || null,
                      latencyMs: intg[name]?.meta?.latencyMs || null,
                      result: intg[name]?.meta?.result || null,
                      loading: intg[name]?.loading,
                    }}
                    onSave={(n, cfg) => save(name, cfg)}
                    onTest={(n, payload) => test(name, payload)}
                  />
                ))}
              </div>

              <Card variant="solid" padding="md">
                <h3 className="font-semibold text-foreground">Agent Hermes · Notifications</h3>
                <p className="mt-2 text-sm text-foreground">Local MCP adapter prepared. Hermes is not connected yet.</p>
                <details className="mt-3 text-sm text-foreground"><summary className="cursor-pointer font-medium">Connection scope</summary><p className="mt-2">Customer and employee notification previews only. No financial reports, expenses, payroll amounts, administrative actions or live message sending. The notification events and delivery channel will be configured in a later step.</p></details>
              </Card>

              {/* Help Card */}
              <Card variant="solid" padding="md">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-foreground">How to Configure</h3>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                      <li>1. Click <strong>Edit</strong> on any integration card</li>
                      <li>2. Enter the required API keys and settings</li>
                      <li>3. Click <strong>Save</strong> to store the configuration</li>
                      <li>4. Click <strong>Test</strong> to verify the connection works</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-3">
                      API keys are stored securely and masked with •••• when saved.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* System & Logs Tab */}
          {activeTab === 'system' && (
            <div className="space-y-6 animate-fade-in">
              <LogsPanel />
            </div>
          )}
        </div>
      </Content>
    </Wrapper>
  )
}

// Helper Components
function QuickCard({ title, icon, children }){
  return (
    <Card variant="solid" hover="none" className="animate-fade-in">
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-lg font-bold text-foreground">{title}</h3>
        {icon && <Icon name={icon} size={24} className="text-primary" />}
      </div>
      <div>{children}</div>
    </Card>
  )
}

function QuickActionCard({ title, description, icon, onClick }){
  return (
    <Card
      variant="solid"
      hover="lift"
      className="cursor-pointer group"
      onClick={onClick}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
            <Icon name={icon} size={22} className="text-primary" />
          </div>
          <Icon name="ArrowRight" size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Card>
  )
}

function StatusDot({ ok }){
  return (
    <span className={`inline-block h-2.5 w-2.5 rounded-full ${ok ? 'bg-success' : 'bg-destructive'}`} />
  )
}
